'use client';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { CommandEmpty, CommandGroup, CommandItem } from '@kit/ui/command';
import { Trans } from '@kit/ui/trans';

import {
  GLOBAL_SEARCH_DISPLAY_ORDER,
  type GlobalSearchKind,
  type GlobalSearchPage,
  type GlobalSearchResult,
} from './global-search-service';
import { resultHref } from './result-link';
import { ResultLeadingVisual, ResultTag } from './result-visual';

/** Heading key (within the `search` namespace) for each entity bucket. */
const GROUP_HEADINGS: Record<GlobalSearchKind, string> = {
  dancer: 'groups.dancers',
  organization: 'groups.organizations',
  audition: 'groups.auditions',
  event: 'groups.events',
};

interface GlobalSearchResultsProps {
  query: string;
  /** The 2-char floor is cleared and a query is in flight. */
  enabled: boolean;
  isLoading: boolean;
  page: GlobalSearchPage | undefined;
  /** Navigate to a result route (dialog owns brand-aware navigation). */
  onNavigate: (href: string) => void;
  /** Navigate to the `/search` page for one entity's full result set. */
  onSeeAll: (kind: GlobalSearchKind) => void;
}

/**
 * Renders the overlay body: one `CommandGroup` per entity (Dancers,
 * Organizations, Competitions & Auditions, Events) with an avatar + title +
 * subtitle row, plus a per-group "See all" item (R2). Server-driven results, so
 * the parent `Command` sets `shouldFilter={false}` — cmdk never hides a row.
 */
export function GlobalSearchResults({
  query,
  enabled,
  isLoading,
  page,
  onNavigate,
  onSeeAll,
}: GlobalSearchResultsProps) {
  const { t } = useTranslation('search');

  if (!enabled) {
    // Empty state: tappable "browse by category" chips (Gojek/Instacart mobile
    // pattern) instead of a bare "type 2 characters" line.
    return (
      <div className="px-4 py-4">
        <p className="text-muted-foreground px-1 pb-2 text-xs font-medium">
          <Trans
            i18nKey="search:browseByCategory"
            defaults="Browse by category"
          />
        </p>
        <div className="flex flex-wrap gap-2">
          {GLOBAL_SEARCH_DISPLAY_ORDER.map((kind) => (
            <Button
              key={kind}
              type="button"
              variant="ghost"
              onClick={() => onSeeAll(kind)}
              className="border-border bg-muted/40 hover:bg-accent hover:text-accent-foreground rounded-full border px-3 py-2 text-sm font-medium transition-colors"
            >
              {t(GROUP_HEADINGS[kind])}
            </Button>
          ))}
        </div>
        <p className="text-muted-foreground px-1 pt-4 text-xs">
          <Trans i18nKey="search:hint" />
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-x-2 py-6 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        <Trans i18nKey="search:loading" />
      </div>
    );
  }

  const total = page?.results.length ?? 0;
  if (total === 0) {
    return <CommandEmpty>{t('noResults', { query })}</CommandEmpty>;
  }

  return (
    <>
      {GLOBAL_SEARCH_DISPLAY_ORDER.map((kind) => {
        const rows = page?.grouped[kind] ?? [];
        if (rows.length === 0) return null;

        return (
          <CommandGroup key={kind} heading={t(GROUP_HEADINGS[kind])}>
            {rows.map((row) => (
              <ResultRow
                key={`${kind}-${row.id}`}
                row={row}
                onNavigate={onNavigate}
              />
            ))}
            <CommandItem
              value={`see-all-${kind}`}
              onSelect={() => onSeeAll(kind)}
              className="text-primary cursor-pointer text-sm font-medium"
            >
              <Trans i18nKey="search:seeAll" />
            </CommandItem>
          </CommandGroup>
        );
      })}
    </>
  );
}

function ResultRow({
  row,
  onNavigate,
}: {
  row: GlobalSearchResult;
  onNavigate: (href: string) => void;
}) {
  const href = resultHref(row);

  // A dancer/org with no handle has no public page: keep it visible in
  // super-admin search, but don't navigate to `/null` (which 404s) — BAL-715.
  return (
    <CommandItem
      value={`${row.kind}-${row.id}`}
      onSelect={href ? () => onNavigate(href) : undefined}
      disabled={!href}
      className={href ? 'cursor-pointer gap-3' : 'gap-3 opacity-60'}
    >
      <ResultLeadingVisual row={row} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="text-foreground truncate font-medium">{row.title}</div>
        {row.subtitle ? (
          <div className="text-muted-foreground truncate text-sm">
            {row.subtitle}
          </div>
        ) : null}
      </div>
      <ResultTag row={row} />
    </CommandItem>
  );
}
